import { QueryResponse } from "~/modules/filters/types";
import {
    getPlatformTableData,
    Platform,
    PlatformTableData,
    PlatformTableDataColumns,
} from "../..";
import { PageHeader } from "~/components/PageHeader";
import { SectionHeader } from "~/components/SectionHeader";
import { RWA_URLS, TOKENIZATION_TYPE_IDS } from "~/consts";
import { Anchor } from "~/components/Anchor";
import { PlatformTable } from "../../components/PlatformTable/component";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { NetFlowsData } from "~/modules/fund_flows/api/queryNetFlows";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    RANK_COLUMN,
    NAME_INDIVIDUAL_COLUMN,
    RWA_ASSET_COUNT_COLUMN,
    STABLECOIN_ASSET_COUNT_COLUMN,
    DISTRIBUTED_MARKET_CAP_COLUMN,
    STABLECOIN_MARKET_CAP_COLUMN,
    RWA_HOLDERS_COUNT_COLUMN,
    STABLECOIN_HOLDERS_COUNT_COLUMN,
    REPRESENTED_MARKET_CAP_COLUMN,
    ASSET_CLASSES_COLUMN,
    ASSOCIATED_NETWORKS_COLUMN,
} from "../../components/PlatformTable/columns";
import { ColumnMetadata } from "~/components/DataGrid/types";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { DEFAULT_PLATFORM_LIST_QUERY } from "../../consts";
import { MetricTimeseriesChart } from "~/modules/visualization/components/MetricTimeseriesChart";
import { MetricTypes } from "~/modules/visualization/components/MetricTimeseriesChart/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { NetFlowsCard } from "~/modules/visualization/components/NetFlowsChart/NetFlowsCard";
import * as Analytics from "../../analytics";

// // // //

type PlatformType = "distributed" | "represented" | "stablecoins";

const PLATFORM_TYPE_DESCRIPTIONS: Record<PlatformType, string> = {
    distributed:
        "Platforms that issue tokens using the blockchain as a distribution layer, enabling onchain investors to subscribe, hold, and manage assets directly through their own wallets or custodians.",
    represented:
        "Platforms that issue tokens using the blockchain as a recordkeeping layer, enhancing transparency, reconciliation, and operational efficiency without enabling onchain investor transfer or distribution.",
    stablecoins:
        "Platforms that issue stablecoins or synthetic assets designed to represent the value of national currencies (e.g., the U.S. dollar), facilitating onchain payments, settlement, and liquidity.",
};

const PLATFORM_TYPE_TITLES: Record<PlatformType, string> = {
    distributed: "Tokenization Platforms",
    represented: "Tokenization Platforms (Represented)",
    stablecoins: "Stablecoin Platforms",
};

const PLATFORM_TYPE_COLUMNS: Record<
    PlatformType,
    ColumnMetadata<PlatformTableData, PlatformTableDataColumns>[]
> = {
    distributed: [
        RANK_COLUMN,
        NAME_INDIVIDUAL_COLUMN,
        RWA_ASSET_COUNT_COLUMN,
        DISTRIBUTED_MARKET_CAP_COLUMN,
        RWA_HOLDERS_COUNT_COLUMN,
        ASSET_CLASSES_COLUMN,
        ASSOCIATED_NETWORKS_COLUMN,
    ],
    represented: [
        RANK_COLUMN,
        NAME_INDIVIDUAL_COLUMN,
        RWA_ASSET_COUNT_COLUMN,
        REPRESENTED_MARKET_CAP_COLUMN,
        ASSET_CLASSES_COLUMN,
        ASSOCIATED_NETWORKS_COLUMN,
    ],
    stablecoins: [
        RANK_COLUMN,
        NAME_INDIVIDUAL_COLUMN,
        STABLECOIN_ASSET_COUNT_COLUMN,
        STABLECOIN_MARKET_CAP_COLUMN,
        STABLECOIN_HOLDERS_COUNT_COLUMN,
        ASSET_CLASSES_COLUMN,
        ASSOCIATED_NETWORKS_COLUMN,
    ],
};

const PLATFORM_TYPE_DEFAULT_SORT_FIELDS: Record<
    PlatformType,
    PlatformTableDataColumns
> = {
    distributed: PlatformTableDataColumns.distributedMarketCap,
    represented: PlatformTableDataColumns.representedMarketCap,
    stablecoins: PlatformTableDataColumns.stablecoinMarketCap,
};

function PlatformTableLayout(props: {
    listQueryResponse: QueryResponse<PlatformTableData>;
    distribution: PlatformType;
}) {
    const { listQueryResponse, distribution } = props;
    const results = listQueryResponse.results;

    const filteredResults = results.filter((p) => {
        if (distribution === "distributed") {
            return p.tokenization_type_ids.includes(
                TOKENIZATION_TYPE_IDS.OnChainDistributed,
            );
        }

        if (distribution === "stablecoins") {
            return (p.stablecoin_market_cap?.val ?? 0) > 0;
        }

        return p.tokenization_type_ids.includes(
            TOKENIZATION_TYPE_IDS.OnChainRepresented,
        );
    });

    const title = PLATFORM_TYPE_TITLES[distribution];
    const columns = PLATFORM_TYPE_COLUMNS[distribution];
    const description = PLATFORM_TYPE_DESCRIPTIONS[distribution];

    return (
        <div className="col-span-12">
            <SectionHeader
                label={title}
                description={description}
                value={`${filteredResults.length.toLocaleString()} Total`}
            >
                <div className="flex justify-end items-center">
                    <DataExportProvider>
                        {({ isLoading, exportCsv }) => (
                            <DownloadButton
                                disabled={isLoading}
                                onClick={() => {
                                    exportCsv({
                                        exportTableName:
                                            ExportTableNames.platforms_v4,
                                        query: DEFAULT_PLATFORM_LIST_QUERY,
                                    });
                                }}
                            />
                        )}
                    </DataExportProvider>
                </div>
            </SectionHeader>

            <PlatformTable
                columns={columns}
                defaultSortField={
                    PLATFORM_TYPE_DEFAULT_SORT_FIELDS[distribution]
                }
                listQueryResponse={{
                    ...listQueryResponse,
                    results: filteredResults,
                    pagination: {
                        ...listQueryResponse.pagination,
                        resultCount: filteredResults.length,
                    },
                }}
            />
        </div>
    );
}

export function PlatformListPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Platform>;
    netFlows: NetFlowsData;
    netFlowsWithStablecoins: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    leagueTableTabsWithoutStablecoins: LeagueTableTabsByDistributionType;
}) {
    const {
        aggregates,
        listQueryResponse,
        netFlows,
        netFlowsWithStablecoins,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    } = props;

    const platforms = listQueryResponse.results;
    const platformTableData = getPlatformTableData(platforms);

    return (
        <div className="flex flex-col h-full grow">
            <PageHeader
                title="Tokenization Platforms"
                subtitle={
                    <>
                        Analyze key performance indicators across tokenization
                        platforms powering tokenized real-world assets. Learn
                        more about tokenized RWAs on the{" "}
                        <Anchor
                            openInNewTab
                            href={RWA_URLS.blog}
                            analyticsLabel={"Platforms - RWA.xyz Blog Link"}
                            className="text-link hover:text-link-hover underline underline-offset-4"
                        >
                            RWA.xyz Research Blog
                        </Anchor>
                        .
                    </>
                }
            />

            <AggregateGrid aggregates={aggregates} />

            <VisualizationGrid className="mt-6">
                <MetricTimeseriesChart
                    title="Tokenization Platform Metrics"
                    analyticsChartName="platform-list-metrics"
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: 12,
                    }}
                    controls={{
                        metrics: {
                            options: [
                                MetricTypes.TotalValue,
                                MetricTypes.AssetHolders,
                                MetricTypes.ActiveAddresses,
                                MetricTypes.TransferVolume,
                                MetricTypes.MintBurnVolume,
                            ],
                        },
                        groupBy: {
                            enabled: false,
                            value: TokenTimeseriesGroupBy.protocol,
                        },
                    }}
                />

                <LeagueTablePieChart
                    title="Platform Asset Value"
                    leagueTableTabs={leagueTableTabs}
                    leagueTableTabsWithoutStablecoins={
                        leagueTableTabsWithoutStablecoins
                    }
                    defaultGroupBy={LeagueTableGroupTypes.platforms}
                    colSpans={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 6,
                        xxl: 6,
                    }}
                    onChangeIncludeStablecoins={({ includeStablecoins }) => {
                        Analytics.trackChangePieChartIncludeStablecoins({
                            includeStablecoins,
                        });
                    }}
                />

                <NetFlowsCard
                    title="Top Net Flows by Platform"
                    netFlows={netFlows}
                    netFlowsWithStablecoins={netFlowsWithStablecoins}
                    onChangeTimeframe={(timeframe) => {
                        Analytics.trackChangeNetFlowsChangeTimeframe({
                            timeframe: timeframe,
                        });
                    }}
                    onChangeIncludeStablecoins={(includeStablecoins) => {
                        Analytics.trackChangeNetFlowsIncludeStablecoins({
                            includeStablecoins: includeStablecoins,
                        });
                    }}
                />
            </VisualizationGrid>

            <VisualizationGrid className="mt-6">
                <PlatformTableLayout
                    distribution="distributed"
                    listQueryResponse={{
                        ...listQueryResponse,
                        results: platformTableData,
                    }}
                />

                <PlatformTableLayout
                    distribution="represented"
                    listQueryResponse={{
                        ...listQueryResponse,
                        results: platformTableData,
                    }}
                />

                <PlatformTableLayout
                    distribution="stablecoins"
                    listQueryResponse={{
                        ...listQueryResponse,
                        results: platformTableData,
                    }}
                />
            </VisualizationGrid>
        </div>
    );
}
